/**
 * Página de Listagem de Pesquisas - Admin
 */

import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { API_CONFIG } from '../../../services/staticDataConfig';

interface PesquisaResumo {
  id: number;
  titulo: string;
  descricao?: string;
  link_publico: string;
  ativa: boolean;
  total_perguntas: number;
  total_respostas: number;
  respostas_completas: number;
  tempo_medio_segundos?: number;
  created_at: string;
  updated_at: string;
}

interface Paginacao {
  pagina: number;
  limite: number;
  total: number;
  totalPaginas: number;
}

export function ListaPesquisas() {
  const navigate = useNavigate();
  const { token } = useAdminAuth();
  
  const [pesquisas, setPesquisas] = useState<PesquisaResumo[]>([]);
  const [paginacao, setPaginacao] = useState<Paginacao>({ pagina: 1, limite: 20, total: 0, totalPaginas: 0 });
  const [loading, setLoading] = useState(true);
  const [filtroAtiva, setFiltroAtiva] = useState<string>('');
  const [filtroTermo, setFiltroTermo] = useState('');
  const [copiadoId, setCopiadoId] = useState<number | null>(null);

  const carregarPesquisas = useCallback(async (pagina = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('pagina', String(pagina));
      params.append('limite', '20');
      
      if (filtroAtiva !== '') {
        params.append('ativa', filtroAtiva);
      }
      if (filtroTermo) {
        params.append('termo', filtroTermo);
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPesquisas(data.dados || []);
        setPaginacao(data.paginacao || { pagina: 1, limite: 20, total: 0, totalPaginas: 0 });
      }
    } catch (error) {
      console.error('Erro ao carregar pesquisas:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filtroAtiva, filtroTermo]);

  useEffect(() => {
    carregarPesquisas();
  }, [carregarPesquisas]);

  const toggleAtiva = async (id: number) => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin/${id}/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        carregarPesquisas(paginacao.pagina);
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
    }
  };

  const duplicarPesquisa = async (id: number) => {
    if (!confirm('Deseja duplicar esta pesquisa?')) return;

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin/${id}/duplicar`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        carregarPesquisas(paginacao.pagina);
        alert('Pesquisa duplicada com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao duplicar:', error);
      alert('Erro ao duplicar pesquisa');
    }
  };

  const excluirPesquisa = async (id: number, titulo: string) => {
    if (!confirm(`Tem certeza que deseja excluir a pesquisa "${titulo}"?\n\nTodas as respostas também serão excluídas!`)) return;

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        carregarPesquisas(paginacao.pagina);
      }
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert('Erro ao excluir pesquisa');
    }
  };

  const copiarLink = (linkPublico: string, id: number) => {
    const url = `${window.location.origin}/pesquisa/${linkPublico}`;
    navigator.clipboard.writeText(url);
    setCopiadoId(id);
    setTimeout(() => setCopiadoId(null), 2000);
  };

  const formatarTempo = (segundos?: number) => {
    if (!segundos) return '-';
    if (segundos < 60) return `${segundos}s`;
    const minutos = Math.floor(segundos / 60);
    const seg = segundos % 60;
    return `${minutos}m ${seg}s`;
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="mb-0">
          <i className="fas fa-clipboard-list me-2"></i>
          Pesquisas / Formulários
        </h2>
        <button
          className="btn btn-success"
          onClick={() => navigate('/admin/pesquisas/nova')}
        >
          <i className="fas fa-plus me-2"></i>
          Nova Pesquisa
        </button>
      </div>

          {/* Filtros */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <input 
                    type="text"
                    className="form-control"
                    placeholder="Buscar por título..."
                    value={filtroTermo}
                    onChange={(e) => setFiltroTermo(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && carregarPesquisas(1)}
                  />
                </div>
                <div className="col-md-3">
                  <select 
                    className="form-select"
                    value={filtroAtiva}
                    onChange={(e) => setFiltroAtiva(e.target.value)}
                  >
                    <option value="">Todas</option>
                    <option value="true">Ativas</option>
                    <option value="false">Inativas</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <button 
                    className="btn btn-primary w-100"
                    onClick={() => carregarPesquisas(1)}
                  >
                    <i className="fas fa-search me-2"></i>
                    Buscar
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Lista */}
          <div className="card">
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary"></div>
                </div>
              ) : pesquisas.length === 0 ? (
                <div className="text-center p-5">
                  <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                  <p className="text-muted">Nenhuma pesquisa encontrada</p>
                  <button 
                    className="btn btn-success"
                    onClick={() => navigate('/admin/pesquisas/nova')}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Criar Primeira Pesquisa
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Título</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Perguntas</th>
                        <th className="text-center">Respostas</th>
                        <th className="text-center">Tempo Médio</th>
                        <th className="text-center">Link</th>
                        <th className="text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pesquisas.map(pesquisa => (
                        <tr key={pesquisa.id}>
                          <td>
                            <Link 
                              to={`/admin/pesquisas/${pesquisa.id}/editar`}
                              className="text-decoration-none"
                            >
                              <strong>{pesquisa.titulo}</strong>
                            </Link>
                            {pesquisa.descricao && (
                              <small className="d-block text-muted text-truncate" style={{ maxWidth: '300px' }}>
                                {pesquisa.descricao}
                              </small>
                            )}
                          </td>
                          <td className="text-center">
                            <span 
                              className={`badge ${pesquisa.ativa ? 'bg-success' : 'bg-secondary'}`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleAtiva(pesquisa.id)}
                              title="Clique para alternar"
                            >
                              {pesquisa.ativa ? 'Ativa' : 'Inativa'}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-info">{pesquisa.total_perguntas}</span>
                          </td>
                          <td className="text-center">
                            <Link to={`/admin/pesquisas/${pesquisa.id}/respostas`}>
                              <span className="badge bg-primary">{pesquisa.total_respostas}</span>
                            </Link>
                          </td>
                          <td className="text-center text-muted">
                            {formatarTempo(pesquisa.tempo_medio_segundos)}
                          </td>
                          <td className="text-center">
                            <button 
                              className={`btn btn-sm ${copiadoId === pesquisa.id ? 'btn-success' : 'btn-outline-secondary'}`}
                              onClick={() => copiarLink(pesquisa.link_publico, pesquisa.id)}
                              title="Copiar link público"
                            >
                              <i className={`fas ${copiadoId === pesquisa.id ? 'fa-check' : 'fa-copy'}`}></i>
                            </button>
                          </td>
                          <td className="text-center">
                            <div className="btn-group btn-group-sm">
                              <Link 
                                to={`/admin/pesquisas/${pesquisa.id}/editar`}
                                className="btn btn-outline-primary"
                                title="Editar"
                              >
                                <i className="fas fa-edit"></i>
                              </Link>
                              <Link 
                                to={`/admin/pesquisas/${pesquisa.id}/estatisticas`}
                                className="btn btn-outline-info"
                                title="Estatísticas"
                              >
                                <i className="fas fa-chart-bar"></i>
                              </Link>
                              <button 
                                className="btn btn-outline-secondary"
                                onClick={() => duplicarPesquisa(pesquisa.id)}
                                title="Duplicar"
                              >
                                <i className="fas fa-copy"></i>
                              </button>
                              <button 
                                className="btn btn-outline-danger"
                                onClick={() => excluirPesquisa(pesquisa.id, pesquisa.titulo)}
                                title="Excluir"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Paginação */}
            {paginacao.totalPaginas > 1 && (
              <div className="card-footer">
                <nav>
                  <ul className="pagination pagination-sm justify-content-center mb-0">
                    <li className={`page-item ${paginacao.pagina === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link"
                        onClick={() => carregarPesquisas(paginacao.pagina - 1)}
                      >
                        Anterior
                      </button>
                    </li>
                    {Array.from({ length: Math.min(5, paginacao.totalPaginas) }, (_, i) => {
                      const inicio = Math.max(1, paginacao.pagina - 2);
                      const pagina = inicio + i;
                      if (pagina > paginacao.totalPaginas) return null;
                      return (
                        <li key={pagina} className={`page-item ${pagina === paginacao.pagina ? 'active' : ''}`}>
                          <button 
                            className="page-link"
                            onClick={() => carregarPesquisas(pagina)}
                          >
                            {pagina}
                          </button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${paginacao.pagina === paginacao.totalPaginas ? 'disabled' : ''}`}>
                      <button 
                        className="page-link"
                        onClick={() => carregarPesquisas(paginacao.pagina + 1)}
                      >
                        Próximo
                      </button>
                    </li>
                  </ul>
                </nav>
                <div className="text-center text-muted small mt-2">
                  Mostrando {pesquisas.length} de {paginacao.total} pesquisas
                </div>
              </div>
            )}
          </div>

    </div>
  );
}

export default ListaPesquisas;
